import { IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import cloneDeep from 'lodash/cloneDeep';
import { FcpTreeItemMapper } from '../mappers/fcpTreeItem.mapper';
import { FcpContent, FcpModel, FcpTreeItemType, Task } from '../models/fcp.model';
import { FcpTreeItem } from '../models/fcpTreeItem.model';

class FcpHelper {
    public static reloadDomainModel(
        domainModel: FcpModel | null,
        finStatementFromMapping: IFinStatements[],
        keepArchivedItems: boolean = false,
    ): FcpModel {
        const updatedDomainModel = cloneDeep<FcpModel | null>(domainModel);
        updatedDomainModel.inputFinStatements = finStatementFromMapping;
        updatedDomainModel.outputFinStatements = finStatementFromMapping;

        const newItems = FcpTreeItemMapper.mapFinstatementToTreeItems(finStatementFromMapping);
        const oldItems = FcpTreeItemMapper.flattenTree(domainModel.items);

        const updateItemsRecursively = (
            newItems: FcpTreeItem[],
            oldItems: FcpTreeItem[],
            archivedItems: FcpTreeItem[],
        ): FcpTreeItem[] => {
            return newItems.map((newItem) => {
                if (newItem.type === FcpTreeItemType.TestItem || newItem.type === FcpTreeItemType.TestGroup) {
                    const oldItem = oldItems.find((old) => old.uid === newItem.uid);

                    if (oldItem) {
                        newItem.comments = oldItem.comments;
                        newItem.documents = oldItem.documents;
                        newItem.closingEntries = oldItem.closingEntries;
                        newItem.tasks = oldItem.tasks;
                        newItem.checklistname = oldItem.checklistname;
                        newItem.isDefaultChecklistDeleted = oldItem.isDefaultChecklistDeleted;
                    } else {
                        const archivedItem = archivedItems.find((archived) => archived.uid === newItem.uid);
                        if (archivedItem) {
                            newItem.comments = archivedItem.comments;
                            newItem.documents = archivedItem.documents;
                            newItem.closingEntries = archivedItem.closingEntries;
                            newItem.tasks = archivedItem.tasks;
                            newItem.checklistname = archivedItem.checklistname;
                            newItem.isDefaultChecklistDeleted = archivedItem.isDefaultChecklistDeleted;
                        }
                    }
                }

                if (newItem.children && newItem.children.length > 0) {
                    newItem.children = updateItemsRecursively(newItem.children, oldItems, archivedItems);
                }
                return newItem;
            });
        };

        const existingArchivedItems = updatedDomainModel.archivedItems ? updatedDomainModel.archivedItems : [];

        const updatedItems = updateItemsRecursively(
            newItems,
            oldItems,
            updatedDomainModel.archivedItems ? updatedDomainModel.archivedItems : [],
        );
        updatedDomainModel.items = updatedItems;

        const newArchivedItems = oldItems.filter(
            (oldItem) => !FcpTreeItemMapper.flattenTree(updatedItems).some((newItem) => newItem.uid === oldItem.uid),
        );

        if (keepArchivedItems) {
            const mergedArchivedItems = [...new Set([...existingArchivedItems, ...newArchivedItems])];
            updatedDomainModel.archivedItems = mergedArchivedItems;
        } else {
            updatedDomainModel.archivedItems = newArchivedItems;
        }

        return updatedDomainModel;
    }

    public static autofillDomainModelFromOtherAnnualReport(
        domainModel: FcpModel | null,
        finStatements: IFinStatements[],
        dueDate: Date,
        dueDatePY: Date,
    ): FcpModel {
        const result = this.reloadDomainModel(domainModel, finStatements, true);
        return this.cleanupTooOldData(result, dueDate, dueDatePY);
    }

    static cleanupTooOldData(result: FcpModel, dueDate: Date, dueDatePY: Date): FcpModel {
        const isOlderThanDueDatePY = (date: Date | string): boolean => {
            return new Date(date) < dueDatePY;
        };

        const filterOldContent = <T extends FcpContent>(contents: T[]): T[] => {
            return contents.filter((content) => !isOlderThanDueDatePY(content.dueDate));
        };

        const resetTasks = (tasks: Task[]): Task[] => {
            return tasks.map((task) => {
                task.isDone = false;
                task.dueDate = null;
                task.doneBy = null;
                task.doneAt = null;
                return task;
            });
        };

        const filterOldItems = (items: FcpTreeItem[]): FcpTreeItem[] => {
            return items.map((item) => ({
                ...item,
                tasks: resetTasks(item.tasks),
                comments: filterOldContent(item.comments),
                documents: filterOldContent(item.documents),
                closingEntries: filterOldContent(item.closingEntries),
                children: filterOldItems(item.children),
                checklistname: item.checklistname,
                isDefaultChecklistDeleted: item.isDefaultChecklistDeleted,
            }));
        };

        result.items = filterOldItems(result.items);
        result.archivedItems = filterOldItems(result.archivedItems);

        return result;
    }
}

export default FcpHelper;
